/**
 * Frontend Logger Utility
 * Only shows logs in development environment, silent in production
 */

class Logger {
  constructor() {
    // Check if we're in development mode
    this.isDevelopment = process.env.REACT_APP_NODE_ENV === 'development';
    
    // Add app name and timestamp to logs
    this.appName = 'LicenseServer-FE';
  }

  /**
   * Format log message with timestamp and app name
   * @param {string} level - Log level (INFO, WARN, ERROR, DEBUG)
   * @param {any} message - Main log message
   * @param {...any} args - Additional arguments
   * @returns {Array} Formatted log arguments
   */
  formatMessage(level, message, ...args) {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${this.appName}] [${level}]`;
    
    if (typeof message === 'string') {
      return [`${prefix} ${message}`, ...args];
    } else {
      return [prefix, message, ...args];
    }
  }

  /**
   * Log info messages
   * @param {any} message - Message to log
   * @param {...any} args - Additional arguments
   */
  info(message, ...args) {
    if (this.isDevelopment) {
      console.log(...this.formatMessage('INFO', message, ...args));
    }
  }

  /**
   * Log warning messages
   * @param {any} message - Message to log
   * @param {...any} args - Additional arguments
   */
  warn(message, ...args) {
    if (this.isDevelopment) {
      console.warn(...this.formatMessage('WARN', message, ...args));
    }
  }

  /**
   * Log error messages (always show in production for debugging)
   * @param {any} message - Message to log
   * @param {...any} args - Additional arguments
   */
  error(message, ...args) {
    // Always log errors, even in production, but with different formatting
    if (this.isDevelopment) {
      console.error(...this.formatMessage('ERROR', message, ...args));
    } else {
      // In production, use simpler error logging for debugging
      console.error(`[${this.appName}] ERROR:`, message, ...args);
    }
  }

  /**
   * Log debug messages (only in development)
   * @param {any} message - Message to log
   * @param {...any} args - Additional arguments
   */
  debug(message, ...args) {
    if (this.isDevelopment) {
      console.debug(...this.formatMessage('DEBUG', message, ...args));
    }
  }

  /**
   * Group logs together
   * @param {string} groupName - Name of the log group
   * @param {Function} callback - Function to execute within the group
   */
  group(groupName, callback) {
    if (this.isDevelopment) {
      console.group(`[${this.appName}] ${groupName}`);
      try {
        callback();
      } finally {
        console.groupEnd();
      }
    } else {
      // In production, just execute the callback without grouping
      callback();
    }
  }

  /**
   * Log API calls and responses
   * @param {string} method - HTTP method
   * @param {string} url - API endpoint
   * @param {any} data - Request/response data
   * @param {string} type - 'request' or 'response'
   */
  api(method, url, data, type = 'request') {
    if (this.isDevelopment) {
      const timestamp = new Date().toISOString();
      const prefix = `[${timestamp}] [${this.appName}] [API]`;
      
      if (type === 'request') {
        console.group(`${prefix} ${method.toUpperCase()} ${url} - REQUEST`);
        if (data) {
          console.log('Request Data:', data);
        }
        console.groupEnd();
      } else {
        console.group(`${prefix} ${method.toUpperCase()} ${url} - RESPONSE`);
        console.log('Response:', data);
        console.groupEnd();
      }
    }
  }

  /**
   * Log performance metrics
   * @param {string} operation - Operation name
   * @param {number} duration - Duration in milliseconds
   */
  performance(operation, duration) {
    if (this.isDevelopment) {
      const formatted = duration > 1000 
        ? `${(duration / 1000).toFixed(2)}s` 
        : `${duration.toFixed(2)}ms`;
      this.info(`⚡ Performance: ${operation} took ${formatted}`);
    }
  }

  /**
   * Log user actions for debugging
   * @param {string} action - Action name
   * @param {any} details - Action details
   */
  userAction(action, details) {
    if (this.isDevelopment) {
      this.info(`👤 User Action: ${action}`, details);
    }
  }

  /**
   * Log component lifecycle events
   * @param {string} component - Component name
   * @param {string} event - Lifecycle event
   * @param {any} props - Component props or additional data
   */
  component(component, event, props) {
    if (this.isDevelopment) {
      this.debug(`🧩 Component: ${component} - ${event}`, props);
    }
  }

  /**
   * Enable/disable logging programmatically
   * @param {boolean} enabled - Whether to enable logging
   */
  setEnabled(enabled) {
    this.isDevelopment = enabled;
  }

  /**
   * Get current environment info
   * @returns {Object} Environment information
   */
  getEnvironment() {
    return {
      isDevelopment: this.isDevelopment,
      nodeEnv: process.env.REACT_APP_NODE_ENV,
      appName: this.appName,
      timestamp: new Date().toISOString()
    };
  }
}

// Create a singleton instance
const logger = new Logger();

// Export both the class and instance for flexibility
export { Logger };
export default logger;